import { lazy } from 'react';
import { handleDynamicImportError } from './dynamicImportHandler';

export const lazyLoad = (importFn, options = {}) => {
  const { maxRetries = 3, retryDelay = 1000 } = options;

  const loadWithRetry = async (retryCount = 0) => {
    try {
      return await importFn();
    } catch (error) {
      console.error(`Dynamic import attempt ${retryCount + 1} failed:`, error);

      const isChunkError =
        error.message?.includes('Failed to fetch dynamically imported module') ||
        error.message?.includes('Loading chunk') ||
        error.message?.includes('ChunkLoadError') ||
        error.name === 'ChunkLoadError';

      if (isChunkError && retryCount < maxRetries) {
        console.warn(`Retrying dynamic import in ${retryDelay}ms... (attempt ${retryCount + 1}/${maxRetries})`);

        await new Promise((resolve) => setTimeout(resolve, retryDelay));

        return loadWithRetry(retryCount + 1);
      }

      await handleDynamicImportError(error);

      throw error;
    }
  };

  return lazy(() => loadWithRetry());
};

export const lazyLoadSimple = (importFn) => {
  return lazy(() =>
    importFn().catch((error) => {
      return handleDynamicImportError(error).then(() => {
        return importFn();
      });
    }),
  );
};

export const lazyLoadWithCustomHandler = (importFn, customErrorHandler) => {
  return lazy(async () => {
    try {
      return await importFn();
    } catch (error) {
      if (customErrorHandler) {
        try {
          await customErrorHandler(error);
          return await importFn();
        } catch (customError) {
          await handleDynamicImportError(error);
          throw error;
        }
      } else {
        await handleDynamicImportError(error);
        throw error;
      }
    }
  });
};
